import { type ReactNode } from "react";
import { ALLERGEN_ICON } from "../data/menu";

type Props = {
  number?: number;          // I, II, III…
  emblem: string;           // emoji or short SVG inside frame
  title: string;
  caption?: string;
  price?: number;
  rotate?: number;
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
  allergens?: string[];
};

const ROMAN = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV","XVI","XVII","XVIII","XIX","XX"];

export function LoteriaCard({ number, emblem, title, caption, price, rotate = -1, className = "", onClick, children, allergens }: Props) {
  return (
    <button
      onClick={onClick}
      className={`relative bg-paper rounded-sm shadow-[0_4px_24px_-8px_rgba(26,22,18,0.25)] active:scale-[0.97] transition-transform group ${className}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      {/* outer double border */}
      <div className="border-2 border-ink/90 m-1.5 rounded-sm">
        <div className="border border-ink/60 p-2 rounded-sm">
          {/* roman numeral */}
          {number !== undefined && (
            <div className="absolute top-0.5 left-1.5 font-mono text-[9px] tracking-widest text-ink/60">№ {ROMAN[number]}</div>
          )}
          {/* emblem area */}
          <div className="aspect-[6/5] flex items-center justify-center bg-sal/40 border border-ink/15 rounded-sm overflow-hidden relative">
            {emblem?.startsWith("/api/img/") || emblem?.startsWith("http")
              ? <img src={emblem} className="absolute inset-0 w-full h-full object-cover" />
              : <span className="text-5xl leading-none drop-shadow-sm">{emblem}</span>}
            <span className="absolute top-0.5 left-0.5 text-ink/30 text-[9px]">✦</span>
            <span className="absolute top-0.5 right-0.5 text-ink/30 text-[9px]">✦</span>
            <span className="absolute bottom-0.5 left-0.5 text-ink/30 text-[9px]">✦</span>
            <span className="absolute bottom-0.5 right-0.5 text-ink/30 text-[9px]">✦</span>
          </div>
          {/* caption banner */}
          <div className="mt-1.5 -mx-0.5 bg-ink text-paper text-center py-1 px-1.5 rounded-sm">
            <div className="font-display font-bold text-sm leading-tight tracking-tight line-clamp-1">{title}</div>
            {caption && <div className="font-mono text-[8px] uppercase tracking-widest opacity-70 mt-0.5 line-clamp-1">{caption}</div>}
          </div>
          {/* price stamp */}
          {price !== undefined && (
            <div className="absolute -top-1.5 -right-1.5 rotate-12 bg-mezcal text-paper font-mono text-xs px-2 py-0.5 rounded-full border-2 border-paper shadow-md">
              ${price}
            </div>
          )}
          {/* allergens row */}
          {allergens && allergens.length > 0 && (
            <div className="flex gap-1 mt-1 justify-center">
              {allergens.slice(0, 5).map((a) => (
                <span key={a} title={a} className="text-xs bg-paper border border-ink/15 rounded-full px-1.5 leading-tight">{ALLERGEN_ICON[a] ?? a}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {children}
    </button>
  );
}
